/**
 * Goals CRUD + deposits — Firestore + dev mode mirror.
 *
 * Wpłata "z portfela" tworzy DODATKOWO transakcję typu 'expense' z kategorią
 * "Cel: <nazwa>" — saldo portfela maleje o kwotę depozytu (zachowuje integralność).
 */
import { addDoc, collection, deleteDoc, doc, updateDoc, Timestamp, serverTimestamp, arrayUnion, writeBatch } from 'firebase/firestore';
import { db, COLLECTIONS, IS_DEV_MODE } from '../../lib/firebase';
import { useGoalsStore, type Goal, type GoalDeposit } from './goalsStore';
import { generateId } from '../../lib/utils';

const GOALS_COLLECTION = COLLECTIONS.goals;

export interface NewGoalInput {
  familyId: string;
  name: string;
  icon: string;
  color: string;
  target: number;
  deadline: Date | null;
}

export async function createGoal(input: NewGoalInput): Promise<string> {
  if (IS_DEV_MODE) {
    const g: Goal = {
      id: generateId('goal'),
      familyId: input.familyId,
      name: input.name,
      icon: input.icon,
      color: input.color,
      target: input.target,
      deadline: input.deadline,
      createdAt: new Date(),
      deposits: [],
    };
    useGoalsStore.getState().addGoal(g);
    return g.id;
  }
  const ref = await addDoc(collection(db, GOALS_COLLECTION), {
    familyId: input.familyId,
    name: input.name,
    icon: input.icon,
    color: input.color,
    target: input.target,
    deadline: input.deadline ? Timestamp.fromDate(input.deadline) : null,
    createdAt: serverTimestamp(),
    deposits: [],
  });
  return ref.id;
}

export async function updateGoal(id: string, patch: Partial<NewGoalInput>): Promise<void> {
  if (IS_DEV_MODE) {
    useGoalsStore.getState().updateGoal(id, {
      ...patch,
      deadline: patch.deadline ?? undefined,
    });
    return;
  }
  const data: Record<string, unknown> = { ...patch };
  if (patch.deadline !== undefined) {
    data.deadline = patch.deadline ? Timestamp.fromDate(patch.deadline) : null;
  }
  await updateDoc(doc(db, GOALS_COLLECTION, id), data);
}

export async function deleteGoal(id: string): Promise<void> {
  if (IS_DEV_MODE) {
    useGoalsStore.getState().removeGoal(id);
    return;
  }
  await deleteDoc(doc(db, GOALS_COLLECTION, id));
}

export interface DepositInput {
  goalId: string;
  goalName: string;
  amount: number;
  type: 'manual' | 'wallet';
  walletId: string | null;
  /** Wymagane dla type=wallet — tworzymy expense tx żeby saldo portfela się zmieniło. */
  familyId?: string;
  userId?: string;
}

export async function depositToGoal(input: DepositInput): Promise<void> {
  const now = new Date();
  const dep: GoalDeposit = {
    id: generateId('dep'),
    amount: input.amount,
    date: now,
    type: input.type,
    walletId: input.walletId,
  };

  if (IS_DEV_MODE) {
    if (input.type === 'wallet' && input.walletId && input.familyId && input.userId) {
      const { useTransactionsStore } = await import('../transactions/transactionsStore');
      useTransactionsStore.getState().addTransaction({
        id: generateId('tx'),
        familyId: input.familyId,
        userId: input.userId,
        type: 'expense',
        amount: input.amount,
        walletId: input.walletId,
        categoryId: null,
        note: `Cel: ${input.goalName}`,
        date: now,
        isRecurring: false,
        syncStatus: 'synced',
      });
    }
    useGoalsStore.getState().addDeposit(input.goalId, dep);
    return;
  }

  // W prod: atomowy batch — tx + deposit razem albo nic.
  const batch = writeBatch(db);
  const depPayload = {
    id: dep.id,
    amount: dep.amount,
    date: Timestamp.fromDate(dep.date),
    type: dep.type,
    walletId: dep.walletId,
  };
  // arrayUnion żeby równoległe deposits się nie nadpisywały (race-safe)
  batch.update(doc(db, GOALS_COLLECTION, input.goalId), {
    deposits: arrayUnion(depPayload),
  });
  if (input.type === 'wallet' && input.walletId && input.familyId && input.userId) {
    const txRef = doc(collection(db, COLLECTIONS.transactions));
    batch.set(txRef, {
      familyId: input.familyId,
      userId: input.userId,
      type: 'expense',
      amount: input.amount,
      walletId: input.walletId,
      toWalletId: null,
      categoryId: null,
      note: `Cel: ${input.goalName}`,
      date: Timestamp.fromDate(now),
      isRecurring: false,
      goalId: input.goalId, // flag dla analytics filter (P1 reviewer note)
      createdAt: serverTimestamp(),
    });
  }
  await batch.commit();
}
